import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';

const RECORD_TYPES = [
  { value: 'stage_1_audit',  label: 'Stage 1 Audit',  desc: 'Document review — assesses readiness',  color: '#fd7e14' },
  { value: 'stage_2_audit',  label: 'Stage 2 Audit',  desc: 'On-site assessment — verifies implementation', color: '#6f42c1' },
  { value: 'certification',  label: 'Issue Certificate', desc: 'Award ISO certification after audits pass', color: '#28a745' },
];

function ISOCertificates() {
  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [companies, setCompanies]       = useState([]);
  const [certBodies, setCertBodies]     = useState([]);
  const [isoStandards, setIsoStandards] = useState([]);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [deleteId, setDeleteId]         = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [activeType, setActiveType]     = useState('stage_1_audit');
  const [formData, setFormData] = useState({
    company_id: '', iso_standard_id: '', cert_body_id: '',
    audit_date: new Date().toISOString().split('T')[0],
    issue_date: new Date().toISOString().split('T')[0],
    expiry_date: '', scope: '', auditor_name: '', audit_days: '',
  });

  const userRole = localStorage.getItem('userRole');
  const isAdmin  = userRole === 'super_admin' || userRole === 'third_party_admin';
  const adminRoles = ['super_admin', 'certification_body', 'third_party_admin'];
  const canIssue = adminRoles.includes(userRole);

  useEffect(() => {
    fetchRecords();
    if (canIssue) { fetchCompanies(); fetchISOStandards(); }
    if (isAdmin)  { fetchCertBodies(); }
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await axiosClient.get('/certifications/certifications/');
      setRecords(Array.isArray(res.data) ? res.data : []);
    } catch { setRecords([]); } finally { setLoading(false); }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axiosClient.get('/clients');
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch { setCompanies([]); }
  };

  const fetchCertBodies = async () => {
    try {
      const res = await axiosClient.get('/cert-bodies/');
      setCertBodies(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch { setCertBodies([]); }
  };

  const fetchISOStandards = async () => {
    try {
      const res = await axiosClient.get('/iso-standards/purchased');
      setIsoStandards(Array.isArray(res.data) ? res.data : []);
    } catch { setIsoStandards([]); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const standard = isoStandards.find(s => s.id === parseInt(formData.iso_standard_id));
      const stdCode = standard?.code || 'ISO';

      const payload = {
        company_id:    parseInt(formData.company_id),
        iso_standard:  stdCode,
        record_type:   activeType,
        issue_date:    formData.issue_date,
        auditor_name:  formData.auditor_name || null,
        scope:         formData.scope || null,
        audit_days:    formData.audit_days ? parseInt(formData.audit_days) : null,
        issued_by_id:  user.id,
        ...(formData.cert_body_id ? { cert_body_id: parseInt(formData.cert_body_id) } : {}),
      };

      if (activeType !== 'certification') {
        payload.audit_date = formData.audit_date || formData.issue_date;
      } else {
        const suffix = Math.random().toString(36).substring(2, 10).toUpperCase();
        payload.certificate_number = `CERT-${new Date().getFullYear()}-${stdCode.replace(/\s/g, '')}-${suffix}`;
        payload.expiry_date = formData.expiry_date ||
          new Date(new Date(formData.issue_date).setFullYear(new Date(formData.issue_date).getFullYear() + 3))
            .toISOString().split('T')[0];
      }

      await axiosClient.post('/certifications/certifications/', payload);
      setShowForm(false);
      setFormData({ company_id: '', iso_standard_id: '', cert_body_id: '', audit_date: new Date().toISOString().split('T')[0], issue_date: new Date().toISOString().split('T')[0], expiry_date: '', scope: '', auditor_name: '', audit_days: '' });
      fetchRecords();
    } catch (err) {
      setError('Failed: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosClient.delete(`/certifications/certifications/${deleteId}/`);
      setDeleteId(null);
      fetchRecords();
    } catch (err) {
      setError('Delete failed: ' + (err.response?.data?.detail || err.message));
      setDeleteId(null);
    } finally { setDeleting(false); }
  };

  const typeInfo = RECORD_TYPES.find(t => t.value === activeType);
  const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' };

  const badgeColor = (type) => {
    if (type === 'stage_1_audit') return { bg: '#fff3cd', color: '#856404' };
    if (type === 'stage_2_audit') return { bg: '#f3f0ff', color: '#6f42c1' };
    return { bg: '#d1fae5', color: '#065f46' };
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;

  if (!canIssue) return (
    <div style={{ padding: '20px' }}>
      <h1>ISO Certification Records</h1>
      <p style={{ color: '#666' }}>You do not have permission to manage ISO certifications.</p>
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0 }}>ISO Certification Records</h1>
          <p style={{ color: '#666', margin: '4px 0 0' }}>Log audits and issue certificates for your clients</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setError(''); }}
          style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          {showForm ? 'Cancel' : '+ Add Record'}
        </button>
      </div>

      {error && <div style={{ background: '#f8d7da', border: '1px solid #dc3545', borderRadius: '4px', padding: '12px', marginBottom: '16px', color: '#842029' }}>{error}</div>}

      {showForm && (
        <div style={{ background: '#f8f9fa', padding: '24px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #dee2e6' }}>
          {/* Record type selector */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            {RECORD_TYPES.map(t => (
              <button key={t.value} type="button" onClick={() => setActiveType(t.value)}
                style={{
                  flex: 1, padding: '14px 12px', borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                  border: `2px solid ${activeType === t.value ? t.color : '#dee2e6'}`,
                  background: activeType === t.value ? `${t.color}15` : 'white',
                }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: t.color }}>{t.label}</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{t.desc}</div>
              </button>
            ))}
          </div>

          <h3 style={{ margin: '0 0 16px', color: typeInfo.color }}>Log {typeInfo.label}</h3>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Client Company *</label>
                <select name="company_id" value={formData.company_id} onChange={e => setFormData(p => ({ ...p, company_id: e.target.value }))} required style={inputStyle}>
                  <option value="">Select client...</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>ISO Standard *</label>
                <select name="iso_standard_id" value={formData.iso_standard_id} onChange={e => setFormData(p => ({ ...p, iso_standard_id: e.target.value }))} required style={inputStyle}>
                  <option value="">Select standard...</option>
                  {isoStandards.map(s => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
                </select>
              </div>
              {isAdmin && certBodies.length > 0 && (
                <div>
                  <label style={labelStyle}>Certification Body *</label>
                  <select value={formData.cert_body_id} onChange={e => setFormData(p => ({ ...p, cert_body_id: e.target.value }))} required={isAdmin} style={inputStyle}>
                    <option value="">Select certification body...</option>
                    {certBodies.map(cb => <option key={cb.id} value={cb.id}>{cb.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={labelStyle}>{activeType === 'certification' ? 'Issue Date' : 'Audit Date'} *</label>
                <input type="date" value={activeType === 'certification' ? formData.issue_date : formData.audit_date}
                  onChange={e => setFormData(p => activeType === 'certification' ? { ...p, issue_date: e.target.value } : { ...p, audit_date: e.target.value, issue_date: e.target.value })}
                  required style={inputStyle} />
              </div>
              {activeType === 'certification' && (
                <div>
                  <label style={labelStyle}>Expiry Date (default: 3 years)</label>
                  <input type="date" value={formData.expiry_date} onChange={e => setFormData(p => ({ ...p, expiry_date: e.target.value }))} style={inputStyle} />
                </div>
              )}
              <div>
                <label style={labelStyle}>Lead Auditor</label>
                <input type="text" value={formData.auditor_name} onChange={e => setFormData(p => ({ ...p, auditor_name: e.target.value }))} placeholder="Auditor name" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Audit Days</label>
                <input type="number" value={formData.audit_days} onChange={e => setFormData(p => ({ ...p, audit_days: e.target.value }))} min="1" style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Scope / Notes</label>
                <textarea value={formData.scope} onChange={e => setFormData(p => ({ ...p, scope: e.target.value }))} rows="2" style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              <button type="submit" disabled={saving}
                style={{ padding: '10px 20px', backgroundColor: saving ? '#ccc' : typeInfo.color, color: 'white', border: 'none', borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving...' : `Log ${typeInfo.label}`}
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ margin: '0 0 16px' }}>All Records</h2>
        {records.length === 0 ? <p style={{ color: '#666' }}>No records yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f8f9fa' }}>
                  {['Type', 'Company', 'ISO Standard', 'Date', 'Auditor', 'Certificate #', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map(r => {
                  const b = badgeColor(r.record_type);
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: b.bg, color: b.color }}>
                          {r.record_type === 'stage_1_audit' ? 'Stage 1 Audit' : r.record_type === 'stage_2_audit' ? 'Stage 2 Audit' : 'Certificate'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{r.company_name || r.company_id}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{r.iso_standard}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{r.audit_date ? new Date(r.audit_date).toLocaleDateString() : new Date(r.issue_date).toLocaleDateString()}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{r.auditor_name || '—'}</td>
                      <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace' }}>{r.certificate_number || '—'}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', background: r.status === 'active' ? '#d1fae5' : '#fee2e2', color: r.status === 'active' ? '#065f46' : '#991b1b', fontWeight: '600' }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button onClick={() => setDeleteId(r.id)}
                          style={{ padding: '4px 10px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', color: '#dc3545' }}>Delete Record?</h3>
            <p style={{ margin: '0 0 20px', color: '#555' }}>This will permanently delete this audit/certificate record. This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: deleting ? 'not-allowed' : 'pointer' }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ISOCertificates;
